import React, { useState } from 'react';
import { ArrowRightIcon, Building2Icon, KeyRoundIcon, LayersIcon, MapPinIcon, PlusIcon, SparklesIcon, SunIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar } from '../components/ui/Avatar';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { PageHeader } from '../components/ui/PageHeader';
import { TbdBadge } from '../components/ui/TbdBadge';
import { Toggle } from '../components/ui/Toggle';
import { adminAccess, departmentLeads, departmentNames, divisionDescriptions, divisionNames, locationAddresses, locationNames } from '../data/org';
import { useHRStore } from '../hooks/useHRStore';
import type { AdminAccess, Navigate } from '../types/hr';
import type { IconComponent } from '../types/ui';
import { displayName, getEmployee } from '../utils/employees';
export function Settings({
  onNavigate
}: {
  onNavigate: Navigate;
}) {
  const {
    employees,
    policies
  } = useHRStore();
  const [access, setAccess] = useState<AdminAccess[]>(adminAccess);
  const update = (id: string, key: 'revealSsn' | 'revealDob', value: boolean) => {
    setAccess(prev => prev.map(a => a.employeeId === id ? {
      ...a,
      [key]: value
    } : a));
    const emp = getEmployee(id);
    toast.success('Access updated', {
      description: `${emp ? displayName(emp) : 'User'} ${value ? 'can now' : 'can no longer'} reveal ${key === 'revealSsn' ? 'SSN' : 'birth date'}. Change logged.`
    });
  };
  const departments = departmentNames.map(name => ({
    name,
    meta: `Lead: ${departmentLeads[name]}`,
    count: employees.filter(e => e.department === name).length
  })).filter(d => d.count > 0);
  const divisions = divisionNames.map(name => ({
    name,
    meta: divisionDescriptions[name],
    count: employees.filter(e => e.division === name).length
  }));
  const locations = locationNames.map(name => ({
    name,
    meta: locationAddresses[name],
    count: employees.filter(e => e.location === name).length
  }));
  return <div data-id="element-1816" data-mp="379ax80ttjkx1">
      <PageHeader eyebrow="Settings" title="Settings & policies" description="Org structure, time off policies, and who can see sensitive data." data-id="element-1817" data-mp="379kpjv2c645w" />

      <div className="space-y-6" data-id="element-1818" data-mp="3g7wmeetspy6s">
        <Card title="Time off policies" subtitle={`${policies.length} active`} icon={SunIcon} action={<Button variant="ghost" size="sm" iconRight={ArrowRightIcon} onClick={() => onNavigate({
        page: 'timeoff',
        tab: 'policies'
      })} data-id="element-1820" data-mp="3q6lslacbxu77">
              Manage policies
            </Button>} data-id="element-1819" data-mp="3g6lb7ty0to5n">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-id="element-1821" data-mp="2srp4qz3lh85">
            {policies.map(p => <button key={p.id} type="button" onClick={() => onNavigate({
            page: 'policy',
            policyId: p.id
          })} className="group flex items-center gap-4 rounded-xl px-4 py-3.5 text-left ring-1 ring-inset ring-slate-200 transition hover:bg-canvas hover:ring-azure/30" data-id="element-1822" data-mp="7smcikjnzoog">
                <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${p.id === 'newhire' ? 'bg-flame/10 text-flame' : 'bg-azure/10 text-azure'}`} data-id="element-1823" data-mp="229w4bvpxdad5">
                  {p.id === 'newhire' ? <SparklesIcon className="h-5 w-5" data-id="element-1824" data-mp="1sanglklnkrz8" /> : <SunIcon className="h-5 w-5" data-id="element-1825" data-mp="1nbbzubladmhn" />}
                </span>
                <span className="min-w-0 flex-1" data-id="element-1826" data-mp="1ndex9gorqhwe">
                  <span className="flex items-center gap-2 text-sm font-semibold text-navy group-hover:text-azure" data-id="element-1827" data-mp="2kzcaaykdpv0y">
                    {p.name} {p.hasTbd && <TbdBadge data-id="element-1828" data-mp="2g0ri9mfa6hnl" />}
                  </span>
                  <span className="block truncate text-xs text-muted" data-id="element-1829" data-mp="2b8hmhprhinde">
                    {employees.filter(e => e.policyId === p.id).length} assigned · {p.tagline}
                  </span>
                </span>
                <ArrowRightIcon className="h-4 w-4 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-azure" data-id="element-1830" data-mp="33qlsdw87ts45" />
              </button>)}
          </div>
        </Card>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3" data-id="element-1831" data-mp="2jfsq59zinpqo">
          <ListCard title="Departments" icon={Building2Icon} items={departments} data-id="element-1832" data-mp="2ofnk5mel8vfy" />
          <ListCard title="Divisions" icon={LayersIcon} items={divisions} data-id="element-1833" data-mp="2odklkdzddtzm" />
          <ListCard title="Locations" icon={MapPinIcon} items={locations} data-id="element-1834" data-mp="29khckuzugoki" />
        </div>

        <Card title="Admin access" subtitle="Who can reveal masked SSN and birth date" icon={KeyRoundIcon} action={<Button size="sm" icon={PlusIcon} onClick={() => toast('Add admin', {
        description: 'Admin roles are granted through Salesforce permission sets.'
      })} data-id="element-1836" data-mp="376y5zp5gii0z">
              Add admin
            </Button>} bodyClassName="pb-2" data-id="element-1835" data-mp="2ei9dufmdpn1d">
          <div className="overflow-x-auto" data-id="element-1837" data-mp="2xeipyxdq03iq">
            <table className="w-full min-w-[640px] text-left text-sm" data-id="element-1838" data-mp="3l5o85j74v7w7">
              <thead data-id="element-1839" data-mp="3pwwdo45ce943">
                <tr className="border-y border-slate-100 bg-canvas/70 text-[11px] font-semibold uppercase tracking-wider text-muted" data-id="element-1840" data-mp="3pxfzvx20zjnx">
                  <th scope="col" className="px-6 py-2.5" data-id="element-1841" data-mp="1egcofldqgzi3">Person</th>
                  <th scope="col" className="px-6 py-2.5" data-id="element-1842" data-mp="19jmb35tbci0d">Role</th>
                  <th scope="col" className="px-6 py-2.5" data-id="element-1843" data-mp="1o7qvce80l1l9">Reveal SSN</th>
                  <th scope="col" className="px-6 py-2.5" data-id="element-1844" data-mp="1o6xj4xqadjvq">Reveal birth date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1845" data-mp="1jb06b1e8evnp">
                {access.map(a => {
                const emp = getEmployee(a.employeeId);
                if (!emp) return null;
                return <tr key={a.employeeId} className="transition-colors hover:bg-azure/[0.03]" data-id="element-1846" data-mp="14k9dpr4tfsjj">
                      <td className="px-6 py-3" data-id="element-1847" data-mp="10hmswa6kb7vu">
                        <button type="button" onClick={() => onNavigate({
                      page: 'profile',
                      employeeId: emp.id
                    })} className="flex items-center gap-3 text-left" data-id="element-1848" data-mp="10guu6qho4u1c">
                          <Avatar name={displayName(emp)} seed={emp.id} size="sm" data-id="element-1849" data-mp="2by2yzcw88c61" />
                          <span data-id="element-1850" data-mp="18mxa1jmgv0y1">
                            <span className="block font-medium text-navy hover:text-azure" data-id="element-1851" data-mp="18lm71m0uhx7z">{displayName(emp)}</span>
                            <span className="block text-xs text-muted" data-id="element-1852" data-mp="1djnpq5uczuix">{emp.jobTitle}</span>
                          </span>
                        </button>
                      </td>
                      <td className="px-6 py-3" data-id="element-1853" data-mp="1mkvosq4p8ze2">
                        <Badge tone={a.role === 'HR Admin' ? 'orange' : 'navy'} data-id="element-1854" data-mp="pvwfr8wajtkt">{a.role}</Badge>
                      </td>
                      <td className="px-6 py-3" data-id="element-1855" data-mp="uu7mvbv7bpzc">
                        <Toggle checked={a.revealSsn} onChange={v => update(a.employeeId, 'revealSsn', v)} label={`${displayName(emp)} can reveal SSN`} srOnlyLabel data-id="element-1856" data-mp="uswj3c1rai88" />
                      </td>
                      <td className="px-6 py-3" data-id="element-1857" data-mp="13om0la032sgc">
                        <Toggle checked={a.revealDob} onChange={v => update(a.employeeId, 'revealDob', v)} label={`${displayName(emp)} can reveal birth date`} srOnlyLabel data-id="element-1858" data-mp="75y7arokzygn" />
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
          <p className="px-6 py-3 text-xs text-muted" data-id="element-1859" data-mp="3uuv4ycdyo231">Every reveal is logged with user, field, employee and timestamp.</p>
        </Card>
      </div>
    </div>;
}
interface ListCardProps {
  title: string;
  icon: IconComponent;
  items: {
    name: string;
    meta: string;
    count: number;
  }[];
}
function ListCard({
  title,
  icon,
  items
}: ListCardProps) {
  const total = items.reduce((sum, i) => sum + i.count, 0);
  return <Card title={title} subtitle={`${items.length} total · ${total} employees`} icon={icon} action={<Button variant="ghost" size="sm" icon={PlusIcon} onClick={() => toast(`Add ${title.slice(0, -1).toLowerCase()}`, {
    description: 'Org structure is managed in Salesforce setup.'
  })} data-id="element-1861" data-mp="371uz44hw6rhs">
          Add
        </Button>} bodyClassName="px-3 pb-3" data-id="element-1860" data-mp="3c1r8ykd9td59">
      <ul className="space-y-0.5" data-id="element-1862" data-mp="2t4ydqikjm28a">
        {items.map(i => <li key={i.name} className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-canvas/70" data-id="element-1863" data-mp="kv8bkdmc802b">
            <span className="min-w-0 flex-1" data-id="element-1864" data-mp="pv36cs98h9sn">
              <span className="block truncate text-sm font-medium text-navy" data-id="element-1865" data-mp="psqo0dzkcxf2">{i.name}</span>
              <span className="block truncate text-xs text-muted" data-id="element-1866" data-mp="busa19p5n303">{i.meta}</span>
            </span>
            <span className="rounded-full bg-canvas px-2.5 py-0.5 text-xs font-semibold text-navy ring-1 ring-inset ring-slate-200" data-id="element-1867" data-mp="gsbtco0pm5yw">{i.count}</span>
          </li>)}
      </ul>
    </Card>;
}